import MediaFire from 'mediafire';
import {Linking} from 'react-native';
import {format, formatRelative, formatDistance} from 'date-fns';
import {getHost, getEnvironment, getClient, setCustomSubdomain, isWeb, isNative, isLocal, isSandbox} from 'utils/platform';
import {setEnvOverride} from 'utils/env';
import {testBit, bytesize} from 'utils/common/data';
import {FileUpload, FileState} from 'utils/uploader';
import {vectors} from 'config/styles';
import {theme} from 'config/themes';
import filesystem from 'extensions/filesystem';
import analytics from 'extensions/analytics';
import storage from 'extensions/storage';
import device from 'extensions/device';
import {LIVE} from 'config/hosts';
import {name} from 'config/app';
import {slices, history} from 'store';
import {AppUserGroup} from 'store/slices/app/types';
import {FilesErrors, FilesItemFlag} from 'store/slices/files/types';

import type {NavigateFunction} from 'react-router';
import type {Dispatch, AnyAction} from '@reduxjs/toolkit';
import type {FolderContent} from 'mediafire/types/Folder';
import type {AppHeader} from 'store/slices/app/types';
import type {Store as FilesStore, FilesItem} from 'store/slices/files/types';

let _accessToken: string;
let _syncFolderTimer: NodeJS.Timeout;
let _syncNotificationsTimer: NodeJS.Timeout;

export let api = new MediaFire(getHost());
export let sessionToken = isLocal() ? import.meta.env.VITE_SESSION_TOKEN : '';
export let sessionEmail = '';
export let sessionPkey = '';
export let sessionEkey = '';

// GENERAL

export async function login(dispatch: Dispatch<AnyAction>, accessToken?: string) {
  _accessToken = accessToken;
  await fetchSession();

  try {
    if (sessionToken) {
      authSession();
      const [header, apiUser, apiAvatar] = await Promise.all([
        fetchCustomization(),
        api.user.getInfo(),
        api.user.getAvatar(),
      ]);

      const avatar = apiAvatar ? apiAvatar.avatar : undefined;
      const {ekey, email, options, created, premium, displayName} = apiUser.userInfo;
      const limit = {storage: {used: apiUser.userInfo['usedStorageSize'], total: apiUser.userInfo['storageLimit']}};
      const group = apiUser.userInfo['oneTimeKeyRequestMaxCount'] === 40000
        ? AppUserGroup.Business
        : premium
          ? AppUserGroup.Pro
          : email
            ? AppUserGroup.Free
            : AppUserGroup.Trial;

      sessionEmail = email;

      // Custom sub domain
      if (header.business)
        setCustomSubdomain(header.business.subdomain);

      // Update app state
      dispatch(slices.app.actions.login({
        ekey,
        email,
        group,
        limit,
        avatar,
        created,
        options,
        header,
        name: displayName,
      }));

      // Notifications
      updateNotifications(dispatch);
      clearTimeout(_syncNotificationsTimer);
      _syncNotificationsTimer = setInterval(async() => {
        updateNotifications(dispatch);
      }, 15 * 1000);

      // Session renewal
      setInterval(async() => {
        renewSession();
      }, 4 * 60 * 1000);

      // Return user details
      return {ekey, email, premium, group};
    }
  } catch(e) {
    return false;
  }
  return false;
}

export async function createSession(credentials: {email: string, password: string}) {
  const body = new FormData();
  body.append('email', credentials.email);
  body.append('password', credentials.password);
  body.append('return_user_cookie', 'true');
  body.append('remember', 'true');
  
  const url = `${getHost()}/application/login.php`;
  const req = await fetch(url, {method: 'post', body});
  const data = await req.json();

  if (data?.response?.error) {
    const {error, remaining, attempts, redirect} = data.response;
    if (redirect) openUrl(redirect);
    switch (error) {
      case 2004: throw new Error(`Login restricted`);
      case 2002: throw new Error(`Too many failed logins`);
      case 2005: throw new Error(`Invalid business domain login`);
      case 2007: throw new Error(`Too many login attempts. Please try again later.`);
      case 2006: throw new Error(`Invalid login. Please check credentials.`);
      case 2003:
        if (attempts !== undefined && remaining !== undefined) {
          throw new Error(`Invalid credentials. ${remaining - attempts} / ${remaining} attempts remaining.`);
        } else {
          throw new Error(`Invalid login. Please check credentials.`);
        }
      default: throw new Error(`Unknown error, please contact support.`);
    }
  } else {
    _accessToken = data.response.user_cookie;
    sessionToken = data.response.session_token;
    filesystem.setSecureItem('access_token', _accessToken);
    authSession();
  }
}

export async function fetchSession() {
  if (!api) {
    api = new MediaFire(getHost());
  }

  let body: FormData;
  if (isNative()) {
    body = new FormData();
    body.append('platform', getClient());
    body.append('user_cookie', _accessToken);
  }

  try {
    const url = `${getHost()}/application/get_session_token.php`;
    const request = await fetch(url, {body, method: 'post', credentials: isNative() ? 'omit' : 'include'});
    const data = await request.json();

    // Login success
    if (data?.response?.session_token) {
      sessionToken = data.response.session_token;
      if (data.response.permanent_token)
        sessionPkey = data.response.permanent_token;
      if (data.response.ekey)
        sessionEkey = data.response.ekey;
      authSession();
      return true;
    }

    // Login failed
    if (data?.response?.error) {
      storage.init(name).clearAll();
    }
  } catch (e) {
    storage.init(name).clearAll();
  }

  return false;
}

export async function renewSession() {
  try {
    const path = `${getHost()}/api/1.5/user/renew_session_token.php`;
    const options = {excludeEndpointUrl: true};
    const response = await api.device.post(path, {sessionToken}, options);
    if (response && response['sessionToken'] && response['sessionToken'] !== sessionToken) {
      sessionToken = response['sessionToken'];
      authSession();
    }
  } catch (e) {
    const error = e?.body?.response?.error;
    if (error === 105 || error === 104)
      fetchSession();
    return undefined;
  }
}

export async function destroySession(dispatch: Dispatch<AnyAction>) {
  try {
    const path = `${getHost()}/application/logout.php`;
    const options = {excludeEndpointUrl: true, withCredentials: true, retries: 5};
    await api.user.post(path, undefined, options);
  } catch (e) {}
  _accessToken = '';
  sessionToken = '';
  dispatch(slices.app.actions.logout());
  storage.init(name).clearAll();
  filesystem.setSecureItem('access_token', '');
  filesystem.clearCookies();
  if (isNative()) {
    device.restartApp();
  } else {
    window.location.href = `${getHost()}/login`;
  }
}

export function switchEnvironment() {
  if (!isNative()) return;
  const next = isSandbox() ? 'live' : 'sandbox';
  setEnvOverride(next);
  _accessToken = '';
  sessionToken = '';
  storage.init(name).clearAll();
  filesystem.setSecureItem('access_token', '');
  filesystem.clearCookies();
  device.restartApp();
}

export function authSession() {
  api.authenticate(sessionToken);
}

// GENERAL

export async function updateNotifications(dispatch: Dispatch<AnyAction>) {
  try {
    const path = `${getHost()}/api/1.5/notifications/peek_cache.php`;
    const options = {excludeEndpointUrl: true};
    const notifications = await api.device.post(path, undefined, options);
    const count = notifications ? parseInt(notifications['numUnread']) : 0;
    dispatch(slices.app.actions.updateNotifications({count}));
  } catch (e) {
    const error = e?.body?.response?.error;
    if (error === 105 || error === 104)
      fetchSession();
    return undefined;
  }
}

export async function fetchCustomization(): Promise<AppHeader> {
  const defaultHeader = {
    needsPaymentUpdate: false,
    business: undefined,
    custom: false,
    logo: '',
    colors: {
      header: theme.app.logo,
      primary: theme.app.logo,
    },
    theme: {
      header: 'default',
    },
  };
  try {
    const path = `${getHost()}/application/customization.php`;
    const options = {excludeEndpointUrl: true, withCredentials: true, retries: 5};
    const response = await api.user.post(path, undefined, options);
    return response ? response as AppHeader : defaultHeader;
  } catch (e) {
    return defaultHeader;
  }
}

export async function openPage(url: string, skipAuth?: boolean) {
  const base = getHost();
  const path = url.includes('://') ? url : `${base}/${url}`;
  if (isNative() && !skipAuth) {
    try {
      const auth = `${base}/api/1.5/user/get_login_token_v2.php`;
      const options = {excludeEndpointUrl: true};
      const response = await api.device.post(auth, {
        password: sessionPkey,
        email: sessionEmail,
        ekey: sessionEkey,
        duration: 'session',
        platform: getClient(),
        redirectPath: path,
      }, options);
      if (response && response['loginToken']) {
        const token = response['loginToken'];
        openUrl(`${base}/dynamic/client_login/token.php?login_token=${token}`);
      }
    } catch (e) {
      openUrl(path);
    }
  } else {
    openUrl(path);
  }
}

export async function openUrl(url: string) {
  try {
    await Linking.openURL(url);
  } catch(e) {
    try {location.href = url;} catch(e) {}
  }
}

export function gotoHomepage() {
  if (isWeb()) {
    window.location.href = getHost();
  }
}

export function trackShare() {
  analytics.gtag('share');
}

export async function fail(
  navigate: NavigateFunction,
  id: string,
  e: any,
) {
  const code = e && e.body && e.body.response ? e.body.response.error : 0;
  switch (code) {
    case FilesErrors.InvalidToken:
    case FilesErrors.MissingToken:
      const success = await fetchSession();
      if (!success) {
        navigate('/login', {state: {from: `/folder/${id}`}});
      }
      break;
    case FilesErrors.InvalidFolderkey:
    case FilesErrors.MissingFolderkey:
    case FilesErrors.AccessDenied:
      if (id) window.location.href = `${getHost()}/error.php?errno=999&quickkey=${id}`;
      break;
    default:
      console.log('error:', code, e);
      break;
  }
}

// FILE MANAGEMENT

export async function move(dispatch: Dispatch<AnyAction>, ids: string[], destination: string) {
  try {
    const files = [];
    const folders = [];
    if (ids.length > 0) {
      ids.forEach(id => isFolderkey(id) ? folders.push(id) : files.push(id));
      await Promise.all([
        files.length && api.file.move(files.join(), destination),
        folders.length && api.folder.move(folders.join(), destination),
      ]);
      dispatch(slices.files.actions.move({ids, destination}));
      analytics.gtag('move');
      return true;
    }
  } catch(e) {
    return e && e.body ? e.body.response : e;
  }
}

export async function copy(dispatch: Dispatch<AnyAction>, ids: string[], destination: string) {
  try {
    const files = [];
    const folders = [];
    if (ids.length > 0) {
      ids.forEach(id => isFolderkey(id) ? folders.push(id) : files.push(id));
      await Promise.all([
        files.length && api.file.post('copy.php', {quickKey: files.join(), folderKey: destination}),
        folders.length && api.folder.post('copy.php', {folderKeySrc: folders.join(), folderKeyDst: destination}),
      ]);
      dispatch(slices.files.actions.copy({ids, destination}));
      analytics.gtag('copy');
      return true;
    }
  } catch(e) {
    return e && e.body ? e.body.response : e;
  }
}

export async function restore(dispatch: Dispatch<AnyAction>, ids: string[], destination: string) {
  try {
    const files = [];
    const folders = [];
    if (ids.length > 0) {
      ids.forEach(id => isFolderkey(id) ? folders.push(id) : files.push(id));
      await Promise.all([
        files.length && api.file.move(files.join(), destination),
        folders.length && api.folder.move(folders.join(), destination),
      ]);
      dispatch(slices.files.actions.move({ids, destination}));
      analytics.gtag('restore');
      return true;
    }
  } catch(e) {
    return e && e.body ? e.body.response : e;
  }
}

export async function archive(dispatch: Dispatch<AnyAction>, ids: string[]) {
  try {
    analytics.gtag('archive');
    const files = ids.filter(id => isQuickkey(id));
    const folders = ids.filter(id => isFolderkey(id));
    dispatch(slices.files.actions.select({ids: []}));
    return await Promise.all([
      files.length > 0 && api.file.post('delete.php', {quickKey: files.join()}),
      folders.length > 0 && api.folder.post('delete.php', {folderKey: folders.join()}),
    ]);
  } catch(e) {
    return e && e.body ? e.body.response : e;
  }
}

export async function purge(dispatch: Dispatch<AnyAction>, ids: string[]) {
  try {
    analytics.gtag('purge');
    const files = ids.filter(id => isQuickkey(id));
    const folders = ids.filter(id => isFolderkey(id));
    dispatch(slices.files.actions.select({ids: []}));
    return await Promise.all([
      files.length > 0 && api.file.post('purge.php', {quickKey: files.join()}),
      folders.length > 0 && api.folder.post('purge.php', {folderKey: folders.join()}),
    ]);
  } catch(e) {
    return e && e.body ? e.body.response : e;
  }
}

export async function rename(id: string, name: string) {
  try {
    analytics.gtag('rename');
    const isFolder = isFolderkey(id);
    const fileType = isFolderkey(id) ? 'folder' : 'file';
    return await api[fileType].post('update.php', isFolder
      ? {folderKey: id, foldername: name}
      : {quickKey: id, filename: name});
  } catch(e) {
    return e && e.body ? e.body.response : e;
  }
}

export async function describe(id: string, description: string) {
  try {
    analytics.gtag('description');
    const isFolder = isFolderkey(id);
    const fileType = isFolderkey(id) ? 'folder' : 'file';
    return await api[fileType].post('update.php', isFolder
      ? {description, folderKey: id}
      : {description, quickKey: id});
  } catch(e) {
    return e && e.body ? e.body.response : e;
  }
}

export async function privacy(ids: string[], state: 'public' | 'private') {
  analytics.gtag('private');
  ids.forEach(async id => {
    const isFolder = isFolderkey(id);
    const fileType = isFolderkey(id) ? 'folder' : 'file';
    try {
      api[fileType].post('update.php', isFolder
        ? {privacy: state, folderKey: id}
        : {privacy: state, quickKey: id});
    } catch(e) {
      return e && e.body ? e.body.response : e;
    }
  });
}

export async function password(id: string, password: string) {
  try {
    analytics.gtag('password');
    const isFolder = isFolderkey(id);
    const fileType = isFolderkey(id) ? 'folder' : 'file';

    // Workaround to fix remove password due to SDK not sending empty string
    if (!password) {
      try {
        const query = `password=${password ? password : ""}&quick_key=${id}&session_token=${sessionToken}`;
        const url = `${getHost()}/api/1.5/file/update_password.php?${query}&response_type=json`;
        const xhr = new XMLHttpRequest();
        xhr.open('POST', url, true);
        xhr.send();
        return {result: 'Success'};
      } catch (e) {
        return {result: 'Error'};
      }
    }

    return await api[fileType].post('update_password.php', isFolder
      ? {folderKey: id, password}
      : {quickKey: id, password});
  } catch(e) {
    return e && e.body ? e.body.response : e;
  }
}

export async function download(url: string, bulk?: boolean, forceDownload?: boolean) {
  if (!url) return;
  const base = bulk ? url : url.replace(/^http:\/\//g, 'https://');
  const path = forceDownload && base ? base.replace('/view/', '/file/') : base;
  try {
    analytics.gtag(bulk ? 'bulk_download' : 'download');
    if (bulk && isWeb()) {
      location.href = path;
    } else if (isWeb() && path && path.indexOf('/view/') === -1) {
      window.open(path);
    } else if (path) {
      openPage(path);
    }
  } catch(e) {
    try {location.href = path;} catch(e) {}
  }
}

export async function preview(id: string, name: string, dispatch: Dispatch, actions: any) {
  try {
    const params = {quickKey: id, linkType: 'direct_download'};
    const response: any = await api.file.post('get_links.php', params);
    return filesystem.viewFile(response.links[0].directDownload, name, dispatch, actions);
  } catch(e) {
    return false;
  }
}

export async function bulkDownload(ids: string[], statusKey: string, confirmBandwidth?: boolean, confirmLargeDownload?: boolean) {
  try {
    const keys = ids.join();
    const meta = await api.file.post('zip_preview.php', {
      keys,
      statusKey,
      metaOnly: 'yes',
      useOwnBandwidth: confirmBandwidth ? 'yes' : 'no',
      allowLargeDownload: confirmLargeDownload ? 'yes' : 'no',
    });
    const authed = sessionToken ? `&session_token=${sessionToken}` : '';
    const query = `use_own_bandwidth=yes&allow_large_download=yes&keys=${keys}&status_key=${statusKey}${authed}`;
    download(`${getHost()}/api/1.5/file/zip.php?${query}`, true);
    return meta;
  } catch(e) {
    return e && e.body ? e.body.response : e;
  }
}

export async function bulkDownloadPreview(ids: string[]) {
  try {
    return await api.file.post('zip_preview.php', {
      keys: ids.join(),
      useOwnBandwidth: 'yes',
      allowLargeDownload: 'yes',
    });
  } catch(e) {
    return e && e.body ? e.body.response : e;
  }
}

export async function bulkDownloadStatus(id: string) {
  try {
    return await api.file.post('zip_status.php', {
      statusKey: id,
      useOwnBandwidth: 'yes',
      allowLargeDownload: 'yes',
    });
  } catch(e) {
    return e && e.body ? e.body.response : e;
  }
}

export async function createFolder(id: string, name: string) {
  try {
    analytics.gtag('create_folder');
    return await api.folder.post('create.php', {
      parentKey: id,
      foldername: name,
    });
  } catch(e) {
    return e && e.body ? e.body.response : e;
  }
}

export async function configureFileDrop(
  action: 'enable' | 'disable' | 'view',
  folderKey: string,
  message?: string,
  embedSize?: string,
  notificationEmails?: string,
) {
  try {
    analytics.gtag('configure_filedrop');
    return await api.folder.post('configure_filedrop.php', {
      action,
      folderKey,
      message,
      embedSize,
      notificationEmails,
    });
  } catch(e) {
    return e && e.body ? e.body.response : e;
  }
}

// FOLDER NAVIGATION

export async function load(
  navigate: NavigateFunction,
  dispatch: Dispatch<AnyAction>,
  id: string,
  init?: boolean,
  sort?: FilesStore['sort'],
  filter?: FilesStore['filter'],
  isFolderTree?: boolean,
) {
  switch (id) {
    case 'following':
      return await loadFollowing(navigate, dispatch);
    case 'recent':
      return await loadRecent(navigate, dispatch);
    case 'trash':
      return await loadTrash(navigate, dispatch);
    default:
      return await loadFolder(navigate, dispatch, id, init, false, false, sort, filter, isFolderTree);
  }
}

export async function loadFolder(
  navigate: NavigateFunction,
  dispatch: Dispatch<AnyAction>,
  id: string,
  isInit?: boolean,
  isSync?: boolean,
  gallery?: boolean,
  sort?: FilesStore['sort'],
  filter?: FilesStore['filter'],
  isFolderTree?: boolean,
) {
  try {
    // Sorting
    let orderBy: 'name' | 'size' | 'created' | 'downloads';
    const orderDir = sort ? sort.order : 'asc';
    if (sort) {
      switch (sort.category) {
        case 'name':
          orderBy = 'name';
          break;
        case 'size':
          orderBy = 'size';
          break;
        case 'date':
          orderBy = 'created';
          break;
        case 'downloads':
          orderBy = 'downloads';
          break;
      }
    }

    // Filtering
    let filterBy: 'application' | 'archive' | 'audio' | 'development' | 'document' | 'image' | 'presentation' | 'spreadsheet' | 'video';
    let filesOnly = false;
    let foldersOnly = false;
    if (filter) {
      switch (filter.type) {
        case 'all':
          filterBy = undefined;
          break;
        case 'files':
          filterBy = undefined;
          filesOnly = true;
          break;
        case 'folders':
          filterBy = undefined;
          foldersOnly = true;
          break;
        case 'private':
          filterBy = 'private' as any; // TODO: update MF-JS-SDK
          break;
        case 'public':
          filterBy = 'public' as any; // TODO: update MF-JS-SDK
          break;
        case 'video':
          filterBy = 'video';
          break;
        case 'audio':
          filterBy = 'audio';
          break;
        case 'images':
          filterBy = 'image';
          break;
        case 'document':
          filterBy = 'document';
          break;
        case 'presentation':
          filterBy = 'presentation';
          break;
        case 'spreadsheet':
          filterBy = 'spreadsheet';
          break;
        case 'development':
          filterBy = 'development';
          break;
      }
    }

    // Retrieve data
    const apiFolder = await api.folder.getInfo(id, 'yes');
    const {name, flag, revision, fileCount, folderCount, totalFiles, totalFolders} = apiFolder.folderInfo;
    const isOwned = testBit(flag, FilesItemFlag.isOwned);
    const folderRoot = isOwned ? {id: 'myfiles', name: 'My Files'} : {id, name};
    const [apiDepth, apiFolders, apiFiles] = await Promise.all([
      isOwned && id !== 'myfiles' && !isSync && !isFolderTree && api.folder.getDepth(id),
      !filesOnly && totalFolders > 0 &&
        api.folder.getContent(id, 'folders', 1, 100, 'yes', orderDir, orderBy, filterBy),
      !isFolderTree && !foldersOnly && totalFiles > 0 &&
        api.folder.getContent(id, 'files', 1, 100, 'yes', orderDir, orderBy, gallery ? 'image' : filterBy),
    ]);

    // Dispatch load
    const details = apiFolder;
    const depth = isOwned && apiDepth && apiDepth['folderDepth'];
    const root = isInit && folderRoot;
    const files = apiFiles.folderContent && apiFiles.folderContent.files as any;
    const folders = apiFolders.folderContent && apiFolders.folderContent.folders as any;
    dispatch(slices.files.actions.load({id, root, depth, details, files, folders}));

    // Load more
    const moreFiles = apiFiles && apiFiles.folderContent.moreChunks;
    const moreFolders = apiFolders && apiFolders.folderContent.moreChunks;
    const moreItems = moreFolders || moreFiles;
    moreItems && !isFolderTree && crawlFolder(navigate, dispatch, id, 2, moreFolders, moreFiles, orderDir, orderBy);

    // Sync folder until we leave it
    !isSync && !isFolderTree && syncFolder(navigate, dispatch, id, revision, fileCount, folderCount, sort);
  } catch(e) {
    fail(navigate, id, e);
  }
}

export async function loadTrash(
  navigate: NavigateFunction,
  dispatch: Dispatch<AnyAction>,
  nochunks?: boolean,
) {
  try {
    // Retrieve data
    const [folders, files] = await Promise.all([
      api.device.getTrash('folders'),
      api.device.getTrash('files'),
    ]);

    // Dispatch load
    dispatch(slices.files.actions.loadTrash({
      id: 'trash',
      revision: 0,
      folderCount: folders ? folders.folderCount : 0,
      fileCount: files ? files.fileCount : 0,
      folders: folders ? folders.folders : [],
      files: folders ? files.files : [],
    }));

    // Load more
    if (!nochunks) {
      const moreFolders = folders && folders.moreChunks;
      const moreFiles = files && files.moreChunks;
      const moreItems = moreFolders || moreFiles;
      moreItems && crawlTrash(navigate, dispatch, 2, moreFolders, moreFiles);
      // Sync trash until we leave it
      syncTrash(navigate, dispatch, 0);
    }

  } catch(e) {
    fail(navigate, 'trash', e);
  }
}

export async function loadSearch(
  navigate: NavigateFunction,
  dispatch: Dispatch<AnyAction>,
  id: string,
  query: string,
) {
  try {
    const search = await api.folder.search(id, query, false);
    const filtered = search?.results?.filter(e => !e.deleteDate);
    const keysFile = filtered ? filtered.filter(e => isQuickkey(e.quickkey)) : [];
    const keysFolder = filtered ? filtered.filter(e => isFolderkey(e.folderkey)) : [];
    const [folders, files] = await Promise.all([
      keysFolder.length > 0 && api.folder.getInfo(keysFolder.map(e => e.folderkey).join()),
      keysFile.length > 0 && api.file.getInfo(keysFile.map(e => e.quickkey).join()),
    ]);
    const infoFolders = folders ? folders['folderInfos'] ? folders['folderInfos'] : [folders.folderInfo] : [];
    const infoFiles = files ? files.fileInfos ? files.fileInfos : [files.fileInfo] : [];
    if (filtered) {
      analytics.gtag('search', {results: filtered.length});
      dispatch(slices.files.actions.loadMulti({folders: infoFolders, files: infoFiles, virtual: 'search'}));
    } else {
      dispatch(slices.files.actions.loadMulti({items: [], virtual: 'search'}));
    }
  } catch(e) {
    dispatch(slices.files.actions.loadMulti({items: [], virtual: 'search'}));
  }
}

export async function loadRecent(
  navigate: NavigateFunction,
  dispatch: Dispatch<AnyAction>,
) {
  try {
    const recent = await api.file.recentlyModified(100);
    const ids = recent && recent.quickkeys && recent.quickkeys.join();
    const info = ids && await api.file.getInfo(ids);
    const files = info ? info.fileInfos ? info.fileInfos : [info.fileInfo] : [];
    dispatch(slices.files.actions.loadMulti({
      virtual: 'recent',
      items: files,
    }));
  } catch(e) {
    fail(navigate, 'recent', e);
  }
}


export async function loadFollowing(
  navigate: NavigateFunction,
  dispatch: Dispatch<AnyAction>,
) {
  try {
    const following = await api.device.post('get_foreign_resources.php') as any;
    dispatch(slices.files.actions.loadMulti({
      virtual: 'following',
      items: [...following.folders, ...following.files],
    }));
  } catch(e) {
    fail(navigate, 'following', e);
  }
}

export async function syncFolder(
  navigate: NavigateFunction,
  dispatch: Dispatch<AnyAction>,
  id: string,
  startRevision: number,
  startFileCount: number,
  startFolderCount: number,
  sort?: FilesStore['sort'],
  filter?: FilesStore['filter'],
) {
  try {
    const apiFolder = await api.folder.getInfo(id);
    const {revision, fileCount, folderCount} = apiFolder.folderInfo;
    const {pathname, hash} = history.location;
    const current = getFolderKeyFromUrl(pathname, hash);
    if (current === id) {
      if ((revision && revision > startRevision)
        || (fileCount && fileCount !== startFileCount)
        || (folderCount && folderCount !== startFolderCount)) {
        loadFolder(navigate, dispatch, id, false, true, undefined, sort, filter);
      }
      clearTimeout(_syncFolderTimer);
      _syncFolderTimer = setTimeout(() =>
        syncFolder(navigate, dispatch, id, revision, fileCount, folderCount, sort, filter), 2500);
    }
  } catch(e) {
    const error = e?.body?.response?.error;
    if (error === 105 || error === 104)
      fetchSession();
  }
}

export async function syncTrash(
  navigate: NavigateFunction,
  dispatch: Dispatch<AnyAction>,
  startRevision: number,
) {
  try {
    const id = 'trash';
    const apiFolder = await api.device.getStatus();
    const revision = apiFolder ? apiFolder['deviceRevision'] : startRevision;
    const {pathname, hash} = history.location;
    const current = getFolderKeyFromUrl(pathname, hash);
    if (current === id) {
      if (revision && revision > startRevision) {
        loadTrash(navigate, dispatch, true);
      }
      clearTimeout(_syncFolderTimer);
      _syncFolderTimer = setTimeout(() =>
        syncTrash(navigate, dispatch, revision), 2500);
    }
  } catch(e) {}
}

export async function crawlFolder(
  navigate: NavigateFunction,
  dispatch: Dispatch<AnyAction>,
  id: string,
  page: number,
  moreFolders: boolean,
  moreFiles: boolean,
  orderDir?: 'asc' | 'desc',
  orderBy?: 'name' | 'size' | 'created' | 'downloads',
) {
  try {
    const size = 100;
    const [folders, files] = await Promise.all([
      moreFolders && api.folder.getContent(id, 'folders', page, size, 'no', orderDir, orderBy),
      moreFiles && api.folder.getContent(id, 'files', page, size, 'no', orderDir, orderBy),
    ]);
    const hasFolders = folders && folders.folderContent && !!folders.folderContent.folders.length;
    const hasFiles = files && files.folderContent && !!files.folderContent.files.length;
    if (hasFolders)
      dispatch(slices.files.actions.chunk({id, page, size, type: 'folders', content: folders}));
    if (hasFiles)
      dispatch(slices.files.actions.chunk({id, page, size, type: 'files', content: files}));
    const loadMoreFolders = hasFolders && folders.folderContent.moreChunks;
    const loadMoreFiles = hasFiles && files.folderContent.moreChunks;
    if (loadMoreFolders || loadMoreFiles) {
      const {pathname, hash} = history.location;
      const current = getFolderKeyFromUrl(pathname, hash);
      if (current === id) {
        return await crawlFolder(navigate, dispatch, id, page + 1, loadMoreFolders, loadMoreFiles, orderDir, orderBy);
      }
    }
    return true;
  } catch(e) {
    return false;
  }
}

export async function crawlTrash(
  navigate: NavigateFunction,
  dispatch: Dispatch<AnyAction>,
  page: number,
  moreFolders: boolean,
  moreFiles: boolean,
) {
  const id = 'trash';
  const size = 100;
  const [folders, files] = await Promise.all([
    moreFolders && api.device.getTrash('folders', page),
    moreFiles && api.device.getTrash('files', page),
  ]);
  const hasFolders = folders && folders.folders && !!folders.folders.length;
  const hasFiles = files && files.files && !!files.files.length;
  if (hasFolders) {
    const type = 'folders';
    const content = {folderContent: folders} as FolderContent;
    dispatch(slices.files.actions.chunk({id, page, size, type, content}));
  }
  if (hasFiles) {
    const type = 'files';
    const content = {folderContent: files} as FolderContent;
    dispatch(slices.files.actions.chunk({id, page, size, type, content}));
  }
  const loadMoreFolders = folders && folders.moreChunks;
  const loadMoreFiles = files && files.moreChunks;
  if (loadMoreFolders || loadMoreFiles) {
    // if (!history.location) return; TODO
    const {pathname, hash} = history.location;
    const current = getFolderKeyFromUrl(pathname, hash);
    if (current === id) {
      return await crawlTrash(navigate, dispatch, page + 1, loadMoreFolders, loadMoreFiles);
    }
  }
  return true;
}

// DOWNLOADING

export function downloadState(items: {[id: string]: FilesItem}) {
  const keys = Object.keys(items);
  const count = keys.length;
  let size = 0;
  let skipped = 0;
  let downloaded = 0;
  let finished = 0;
  let completed = true;
  keys.forEach(id => {
    const file = items[id];
    if (file) {
      const status = file.uploadStatus || '';
      const isComplete = status === 'Completed';
      const hasError = isNaN(parseInt(status))
        && status !== 'Preparing…'
        && status !== 'Packaging'
        && status !== 'Completed';
      if (hasError) {
        skipped++;
        return;
      }
      size += (file.size || 0);
      downloaded += isComplete ? file.size : (file.uploadProgress || 0);
      if (isComplete)
        finished++;
      if (!hasError && !isComplete)
        completed = false;
    }
  });
  return {
    size,
    count,
    skipped,
    downloaded,
    finished,
    completed,
  };
}

export function downloadSync(
  dispatch: Dispatch<AnyAction>,
  downloads: {
    bytes: number,
    total: number,
    state: string,
    name: string,
    id: string,
  }[],
) {
  const items: FilesItem[] = [];
  downloads && downloads.forEach(download => {
    let downloadProgress = 0;
    let downloadStatus = '';
    let downloadFailure = false;
    switch(download.state) {
      case 'starting file':
      case 'writing file':
        downloadProgress = download.bytes;
        downloadStatus = download.bytes ? `${((download.bytes / download.total) * 100).toFixed(2)}%` : '0%';
        break;
      case 'finalizing zip':
        downloadProgress = download.total;
        downloadStatus = 'Packaging';
      case 'complete':
        downloadProgress = download.total;
        downloadStatus = 'Completed';
        break;
      case 'skipped file':
        downloadStatus = 'Skipped file';
        downloadFailure = true;
        break;
      default:
        downloadStatus = 'Preparing…';
        break
    }
    items.push({
      type: 'archive',
      id: download.id,
      name: download.name,
      size: download.total,
      uploadStatus: downloadStatus,
      uploadProgress: downloadProgress,
      uploadFailure: downloadFailure,
      created: '',
      hierarchy: [],
      private: true,
      revision: 0,
      state: 0,
      url: '',
    });
  });

  if (items.length > 0) {
    dispatch(slices.transfers.actions.downloadSync({items}));
  }
}

// UPLOADING

export function uploadState(items: {[id: string]: FilesItem}) {
  const keys = Object.keys(items);
  const count = keys.length;
  let size = 0;
  let hashed = 0;
  let aborted = 0;
  let skipped = 0;
  let uploaded = 0;
  let finished = 0;
  let verifying = 0;
  let completed = true;
  keys.forEach(id => {
    const file = items[id];
    if (file) {
      const status = file.uploadStatus || '';
      const isAborted = status === 'Skipped';
      const isPolling = status === 'Verifying…';
      const isComplete = status === 'Completed';
      const isTransferred = isComplete || isPolling;
      const hasError = isNaN(parseInt(status))
        && status !== 'Queued'
        && status !== 'Preparing…'
        && status !== 'Verifying…'
        && status !== 'Completed'
        && status !== 'Conflict';
      if (isAborted)
        aborted++;
      if (hasError) {
        skipped++;
        return;
      }
      size += (file.size || 0);
      hashed += isTransferred ? file.size : (file.uploadHashed || 0);
      uploaded += isTransferred ? file.size : (file.uploadProgress || 0);
      if (isComplete)
        finished++;
      if (isPolling)
        verifying++;
      if (!hasError && !isComplete)
        completed = false;
    }
  });
  return {
    size,
    count,
    aborted,
    skipped,
    uploaded,
    hashed,
    finished,
    verifying,
    completed,
  };
}

export function uploadSync(
  dispatch: Dispatch<AnyAction>,
  file: FileUpload,
  error?: string,
) {
  let uploadHashed = file.transfer.hashed || 0;
  let uploadProgress = file.transfer.uploaded || 0;
  let uploadFailure = false;
  const status = `${((uploadProgress / file.info.size) * 100).toFixed(2)}%`;
  let uploadStatus = status === '0.00%' ? 'Queued' : status;
  if (file.state === FileState.Completed)
    analytics.gtag('upload');
  switch (file.state) {
    case FileState.Queued:
      uploadStatus = 'Queued';
      break;
    case FileState.Hashing:
      uploadStatus = 'Preparing…';
      break;
    case FileState.Verifying:
      uploadStatus = 'Verifying…';
      break;
    case FileState.Completed:
      uploadStatus = 'Completed';
      break;
    case FileState.Conflict:
      uploadStatus = 'Conflict';
      break;
    case FileState.Duplicate:
      uploadStatus = 'Duplicate';
      break;
    case FileState.Aborted:
      uploadStatus = 'Skipped';
      uploadFailure = true;
      break;
    case FileState.Failed:
      uploadStatus = error ? error.toString() : 'Upload error (-1)';
      uploadFailure = true;
      break;
  }
  dispatch(slices.transfers.actions.uploadSync({
    id: file.info.dest,
    items: [{
      name: file.info.name,
      type: file.info.type,
      size: file.info.size,
      uploadStatus,
      uploadHashed,
      uploadProgress,
      uploadFailure,
      id: file.id,
      url: getShareLink(
        file.quickkey,
        file.info.type,
        file.info.name,
      ),
      hierarchy: [],
      private: true,
      revision: 0,
      state: 0,
      created: '',
    }],
  }));
}

export function uploadWebSync(
  dispatch: Dispatch<AnyAction>,
  destination: string,
  uploads: {
    url: string,
    eta: string,
    size: number,
    status: string,
    active: boolean,
    quickkey: string,
    filename: string,
    uploadkey: string,
    createdUtc: string,
    percentage: number,
    statusCode: number,
    errorStatus: number,
  }[],
) {
  const items: FilesItem[] = [];
  uploads && uploads.forEach(upload => {
    const size = upload.size || 0;
    let uploadProgress = 0;
    let uploadStatus = '';
    let uploadFailure = false;
    if (upload.errorStatus === 0) {
      switch(upload.statusCode) {
        case 1:
        case 2:
          uploadStatus = 'Preparing…';
          break;
        case 3:
        case 4:
          uploadProgress = size > 0 ? (size / 100) * upload.percentage : 0;
          uploadStatus = `${upload.percentage.toFixed(2)}%`;
          break;
        case 6:
          uploadProgress = size;
          uploadStatus = 'Verifying…';
          break;
        case 99:
          uploadProgress = size;
          uploadStatus = 'Completed';
          break;
      }
    } else {
      uploadFailure = true;
      switch(upload.errorStatus) {
        case 9:
          uploadStatus = 'Failed (9)';
          break;
        case 10:
          uploadStatus = 'Failed (too big)';
          break;
        case 11:
          uploadStatus = 'Failed (too small)';
          break;
        case 14:
          uploadStatus = 'Failed (14)';
          break;
        case 15:
          uploadStatus = 'Failed (invalid url)';
          break;
        case 16:
          uploadStatus = 'Failed (missing file)';
          break;
        case 17:
          uploadStatus = 'Failed (image too big)';
          break;
        case 19:
          uploadStatus = 'Failed (virus found)';
          break;
        case 38:
          uploadStatus = 'Failed (partial file)';
          break;
        case 39:
          uploadStatus = 'Failed (storage full)';
          break;
      }
    }

    items.push({
      size,
      uploadStatus,
      uploadProgress,
      uploadFailure,
      id: upload.quickkey,
      name: upload.filename,
      created: upload.createdUtc,
      hierarchy: [],
      private: true,
      revision: 0,
      state: 0,
      url: '',
      // TODO: get type from api, quick hack
      type: upload.filename.match(/\.(jpg|jpeg|png|gif|bmp|tiff)$/i)
        ? 'image'
        : upload.filename.match(/\.(aac|wav|ogg|mp3|flac|wma)$/i)
          ? 'music'
          : upload.filename.match(/\.(mp4|mkv|mv4|avi|mov|webm|flv|wmv)$/i)
            ? 'video'
            : 'file',
    });
  });

  if (items.length > 0) {
    dispatch(slices.transfers.actions.uploadSync({items, id: destination}));
  }
}

export async function uploadAddWebFile(dispatch: Dispatch<AnyAction>, id: string, url: string, name: string) {
  try {
    analytics.gtag('webupload');
    const path = `${getHost()}/api/1.5/upload/add_web_upload.php`;
    const options = {excludeEndpointUrl: true};
    const params = {url, folderKey: id, filename: name};
    const response = await api.device.post(path, params, options);
    const uploadKey = response && response['uploadKey'];
    dispatch(slices.transfers.actions.uploadWeb({id, files: [uploadKey]}));
    return await response;
  } catch(e) {
    return e && e.body ? e.body.response : e;
  }
}

export async function uploadGetWebFiles(uploadKey?: string) {
  try {
    const path = `${getHost()}/api/1.5/upload/get_web_uploads.php`;
    const options = {excludeEndpointUrl: true};
    const params = {uploadKey, allWebUploads: 'yes'};
    return await api.device.post(path, params, options);
  } catch(e) {
    return e && e.body ? e.body.response : e;
  }
}

// UTILITIES

export function isQuickkey(id: unknown) {
  const LENGTH_QUICKKEY = 15;
  const LENGTH_QUICKKEY_OLD = 11;
  if (!id || typeof id !== 'string') return false;
  if (id.length !== LENGTH_QUICKKEY && id.length !== LENGTH_QUICKKEY_OLD) return false;
  if (/[^a-z0-9]/.test(id)) return false;
  return true;
}

export function isFolderkey(id: unknown) {
  const LENGTH_FOLDERKEY = 13;
  const key = id && id.toString();
  if (!key) return false;
  if (key === 'myfiles'
    || key === 'trash'
    || key === 'recent'
  ) return true;
  if (key.length !== LENGTH_FOLDERKEY) return false;
  if (/[^a-z0-9]/.test(key)) return false;
  return true;
}

export function isFolderSpecial(id: string) {
  return id === 'myfiles'
    || id === 'trash'
    || id === 'recent'
    || id === 'following';
}

export function getShareLink(id: string, type?: string, name?: string) {
  if (!id) return '';
  const slug = name && name.length ? '/' + name.split(' ').join('+') : '';
  const base = getHost();
  if (!type) type = isFolderkey(id) ? 'folder' : '';
  switch (type) {
    case 'folder':
      return `${base}/folder/${id}${slug}`;
    case 'image':
      return `${base}/view/${id}${slug}/file`;
    default:
      return `${base}/file/${id}${slug}/file`;
  };
}

export function getFolderKeyFromUrl(path: string, hash?: string) {
  for (const pattern of [
    /#\/([a-z0-9]+)/,         // hash
    /#([a-z0-9]+)/,           // legacy
    /([a-z0-9]+)/,            // normal
    /\/folder\/([a-z0-9]+)/,  // shared
    /\/file\/([a-z0-9]+)/,    // download
    /\/content\/([a-z0-9]+)/, // unicorn
    /\/files\/([a-z0-9]+)/,   // reserved
    /\/myfiles\/([a-z0-9]+)/, // reserved
  ]) {
    const pathMatch = path && path.match(pattern);
    const pathKey = pathMatch && pathMatch.pop();
    if (isFolderkey(pathKey)) return pathKey;
    const hashMatch = hash && hash.match(pattern);
    const hashKey = hashMatch && hashMatch.pop();
    if (isFolderkey(hashKey)) return hashKey;
  }
  return false;
}

export function parseContentItem(item: any, hierarchy?: any, chain?: any, cache?: any) {
  const id = item.quickkey || item.folderkey;
  const type = item.filetype || 'folder';
  const name = item.filename || item.name;
  const size =  item.totalSize && item.totalSize > item.size
    ? item.totalSize
    : (item.size || 0);
  const url = item.links
    ? (item.links.view || item.links.normalDownload)
    : getShareLink(id, type, name);

  let files = item.totalFiles > item.fileCount ? item.totalFiles : (item.fileCount || 0);
  let folders = item.totalFolders > item.folderCount ? item.totalFolders : (item.folderCount || 0);

  // Workaround for empty root folders that had previous items
  if (id === 'myfiles') {
    if (item.folderCount === 0 && item.totalFolders > 0)
      folders = 0;
    if (item.fileCount === 0 && item.totalFiles > 0)
      files = 0;
  }

  const parent = hierarchy ? hierarchy[hierarchy.length - 1] : undefined;
  const parentKey = parent ? parent[0] : undefined;
  const parentName = parent ? parent[1] : undefined;

  return {
    id,
    url,
    name,
    type,
    size,
    files,
    folders,
    state: 0,
    flag: item.flag || 0,
    hash: item.hash || '',
    downloads: item.downloads || 0,
    filedrop: item.dropboxEnabled || false,
    owner: item.ownerName,
    revision: item.revision,
    created: item.createdUtc,
    deleted: item.deleteDate,
    parentKey: item.parentFolderkey || parentKey,
    parentName: item.parentName || parentName,
    shared: item.dateSharedUtc,
    description: item.description,
    password: !!item.passwordProtected,
    private: item.privacy === 'private',
    shareLinkEnabled: item.shareLinkEnabled,
    hierarchy: hierarchy ? [...hierarchy, [id, name]]
      : chain ? [...chain.reverse().map((f: any) => [f.folderkey, f.name])]
      : cache ? cache.hierarchy : [],
  };
}

export function copyLink(link: string) {
  analytics.gtag('copy_link');
  device.clipboardSet(link);
}

export function getTimeStamp(time: string) {
  const invalid = '-';
  if (!time) return invalid;
  try {
    return format(new Date(time.replace(/\s/g, 'T')), 'yyyy-MM-dd HH:mm:ss');
  } catch(e) {
    return invalid;
  }
}

export function getTimeSince(time: string) {
  const invalid = '-';
  if (!time) return invalid;
  try {
    return formatDistance(new Date(time.replace(/\s/g, 'T')), new Date());
  } catch(e) {
    return invalid;
  }
}

export function getTimeRelative(time: string) {
  const invalid = '-';
  if (!time) return invalid;
  try {
    return formatRelative(new Date(time.replace(/\s/g, 'T')), new Date());
  } catch(e) {
    return invalid;
  }
}

export function getDetails(item: any, limited: boolean = false) {
  if (item.type === 'folder') {
    if (limited) return '';
    const folderCount = item.folders >= 9999 ? `many` : item.folders;
    const fileCount = item.files >= 9999 ? `many` : item.files;
    const folders = `${folderCount} ${item.folders === 1 ? 'folder' : 'folders'}`;
    const files = `${fileCount} ${item.files === 1 ? 'file' : 'files'}`;
    return `${folders}, ${files}`;
  } else {
    const size = bytesize(item.size);
    if (item.downloads === undefined || limited) return size;
    const downloadCount = item.downloads >= 999999 ? `many` : item.downloads;
    const downloads = `${downloadCount} download${item.downloads === 1 ? '' : 's'}`;
    return `${downloads}, ${size}`;
  }
}

export function getPhoto(item: any, size: 'small' | 'medium' | 'large' = 'medium') {
  if (!testBit(item.flag, FilesItemFlag.isViewable)) return '';
  const env = getEnvironment();
  const subdomain = env === 'www' ? `www${parseInt(item.hash.charAt(0), 16) + 1}` : env;
  const host = (LIVE).replace('www', subdomain);
  const authed = (item.private || item.shareLinkEnabled === false) && sessionToken ? `?session_token=${sessionToken}` : '';
  
  // Map size to MediaFire thumbnail size codes
  const sizeMap = {
    small: '1g',    // Small thumbnail
    medium: '3g',   // Medium thumbnail (default)
    large: '5g'     // Large thumbnail
  };
  
  const thumbnailSize = sizeMap[size] || sizeMap.medium;
  return `${host}/convkey/${item.hash.substr(0, 4)}/${item.id}${thumbnailSize}.jpg${authed}`;
}

export function getKeysFromUrl(url: string) {
  const parts = url.split(getHost());
  const keys: string[] = [];
  let found: boolean;
  const append = (url: string) => {
    if (isFolderkey(url) || isQuickkey(url)) {
      keys.push(url);
      found = true;
    }
  };

  parts.forEach(url => {
    const segments = url.split('/');
    for (let i = 0; i < segments.length; i++) {
      const segment = segments[i];
      const urls = segment.split(',');
      found = false;
      urls.forEach(append);
      if (found) break;
    }
  });

  return keys;
}

export function getIcon(type: string, name: string) {
  if (name && name.length && name.includes('.pdf'))
    return {vector: vectors.files.pdf, type: 'pdf'};

  let vector = vectors.files.generic;

  switch (type) {
    case 'folder':
      vector = vectors.files.folder;
      break;
    case 'text':
      vector = vectors.files.text;
      break;
    case 'code':
      vector = vectors.files.code;
      break;
    case 'archive':
      vector = vectors.files.archive;
      break;
    case 'audio':
      vector = vectors.files.audio;
      break;
    case 'video':
      vector = vectors.files.video;
      break;
    case 'image':
      vector = vectors.files.image;
      break;
    case 'spreadsheet':
      vector = vectors.files.spreadsheet;
      break;
    case 'pdf':
      vector = vectors.files.pdf;
      break;
    case 'presentation':
      vector = vectors.files.presentation;
      break;
    case 'document':
      vector = vectors.files.document;
      break;
  };

  return {vector, type};
}
